import { Injectable } from '@angular/core';
import {
  AbstractControl,
  AbstractControlOptions,
  UntypedFormArray,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { PrFormAbstract } from '@app/components/pr-form/pr-form.abstract';
import { PledgeProductFinancialClauseBeneficiaireBeneficiaire } from '@app/forms/subscription/definition/interface/subscription';
import { FormConfig } from '@app/forms/subscription/definition/types/subscription';
import * as _ from 'lodash';
import { PledgeProductRead } from '../../definition/interface/Pledge';
import FormGroupArray from '../../services/FormGroupArray';
@Injectable({
  providedIn: 'root',
})
export abstract class EaSubscriptionBuilder extends PrFormAbstract {
  generateForm(entity: PledgeProductRead & { parameters: any }) {
    let options: AbstractControlOptions = {};

    const parametersGroup = this.formBuilder.group({
      avClauseBeneficiaire: [
        entity?.parameters?.avClauseBeneficiaire || 'STANDARD',
        [
          this.prFormService.RefreshEveryValidators([
            'avClauseBeneficiaireStandard',
          ]),
          Validators.required,
        ],
      ],
      avClauseBeneficiaireStandard: [
        entity?.parameters?.avClauseBeneficiaireStandard || null,
        [
          this.prFormService.AddValidatorsBasedOnCondition(
            this.prFormService
              .isTargetControlValueEqualTo(
                'avClauseBeneficiaire',
                val => val === 'STANDARD',
              )
              .bind(this),
            Validators.required,
          ),
        ],
      ],
      avClauseBeneficiaireLibre: [
        entity?.parameters?.avClauseBeneficiaireLibre || null,
      ],
      avClauseBeneficiaireNomNotaire: [
        entity?.parameters?.avClauseBeneficiaireNomNotaire || null,
      ],
      avClauseBeneficiairePrenomNotaire: [
        entity?.parameters?.avClauseBeneficiairePrenomNotaire || null,
      ],

      avClauseBeneficiaireAddresseNotaire: this.formBuilder.group({
        street: [
          entity?.parameters?.avClauseBeneficiaireAddresseNotaire?.street ||
            null,
        ],
        zipCode: [
          entity?.parameters?.avClauseBeneficiaireAddresseNotaire?.zipCode ||
            null,
        ],
        city: [
          entity?.parameters?.avClauseBeneficiaireAddresseNotaire?.city || null,
        ],
        country: [
          entity?.parameters?.avClauseBeneficiaireAddresseNotaire?.country ||
            null,
        ],
      }),
      avClauseBeneficiaireBeneficiaires: this.formBuilder.array([
        this.formGroup(),
      ]),
    });

    const formConfig: FormConfig<PledgeProductRead & { parameters: any }> = {
      id: [entity.id],
      type: [entity.type],
      productId: [entity.productId],
      productName: [entity.productName],
      status: [entity.status],
      parameters: parametersGroup,
    };

    const formBuilderGroup = this.formBuilder.group(formConfig, options);
    entity?.parameters?.avClauseBeneficiaireBeneficiaires?.length > 0 &&
      parametersGroup.setControl(
        'avClauseBeneficiaireBeneficiaires',
        this.populate(entity?.parameters),
      );
    entity?.parameters?.avClauseBeneficiaireBeneficiaires?.length > 0 &&
      parametersGroup
        .get('avClauseBeneficiaireBeneficiaires')
        .setValidators(this.percentageSumValidator());

    return formBuilderGroup;
  }
  generate(entity: any, option) {
    return FormGroupArray.generate({
      entity,
      options: option,
      formBuilder: this.formBuilder,
      generateForm: this.generateForm.bind(this),
      propertyName: 'products',
    });
  }

  populate(data: PledgeProductFinancialClauseBeneficiaireBeneficiaire) {
    let formArray = this.formBuilder.array([this.formGroup()]);
    if (data) {
      formArray.clear();
      data.avClauseBeneficiaireBeneficiaires.forEach((item, index) => {
        formArray.push(
          this.formBuilder.group({
            civility: [item?.civility || null],
            firstName: [item?.firstName || null, Validators.required],
            lastName: [item?.lastName || null, Validators.required],
            maidenName: [item?.maidenName || null],
            email: [item?.email || null],
            birthDate: [item?.birthDate || null],
            birthPlace: [item?.birthPlace || null],
            phone: [item?.phone || null],
            percentage: [item?.percentage || null, Validators.required],
            address: this.formBuilder.group({
              street: [item?.address?.street || null],
              street2: [item?.address?.street2 || null],
              zipCode: [item?.address?.zipCode || null],
              city: [item?.address?.city || null],
              country: [item?.address?.country || null],
            }),
          }),
        );
      });
    }
    return formArray;
  }
  formGroup(): UntypedFormGroup {
    return this.formBuilder.group({
      civility: [null],
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      maidenName: [''],
      email: [''],
      birthDate: [null],
      birthPlace: [''],
      phone: [''],
      percentage: [100, Validators.required],
      address: this.formBuilder.group({
        street: [null, Validators.required],
        street2: [null],
        zipCode: [null, Validators.required],
        city: [null, Validators.required],
        country: [null],
      }),
    });
  }

  get products() {
    return this.form.get('products') as UntypedFormArray;
  }
  extract() {
    const products = _.cloneDeep(this.form.value.products);

    products.forEach(product => {
      if (!!product?.parameters?.avClauseBeneficiaireBeneficiaires?.length) {
        product.parameters.avClauseBeneficiaireBeneficiaires.forEach(
          beneficiary => {
            beneficiary.phone = _.isObject(beneficiary.phone)
              ? beneficiary?.phone?.e164Number
              : beneficiary?.phone;
          },
        );
      }
    });

    return products;
  }

  percentageSumValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const beneficiaries = control.value;
      if (!Array.isArray(beneficiaries)) {
        return null;
      }

      const totalPercentage = beneficiaries.reduce((sum, beneficiary) => {
        return sum + (beneficiary.percentage || 0);
      }, 0);

      return totalPercentage === 100
        ? null
        : {
            message: 'La somme des pourcentages doit être égale à 100%',
          };
    };
  }
}
